import type { TablePaginationConfig } from "antd";
import dayjs from "dayjs";
import { useI18n } from "@/i18n";
import { useSetUrlFilters, useUrlFilter } from "@/lib/useUrlFilter";

const SIZES = ["25", "50", "100", "200"] as const;
type Size = (typeof SIZES)[number];
const DAY = "YYYY-MM-DD";

/**
 * Page and page size of a list, kept in the URL (?page, ?size) like its
 * filters, and the antd pagination for the server's {total, totalCapped}
 * (Plan §6.15). Counting stops at 10,000: past that the pager says
 * "10.000+" and the last pages are reached by narrowing the filters.
 */
export function useListPage(defaultSize: Size = "50") {
  const { t, formatNumber } = useI18n();
  const [pageRaw, setPageRaw] = useUrlFilter<string>("page", "1");
  const [size, setSize] = useUrlFilter<Size>("size", defaultSize, SIZES);
  const page = Math.max(1, Number.parseInt(pageRaw, 10) || 1);
  const limit = Number(size);
  const query = { limit, ...(page > 1 ? { offset: (page - 1) * limit } : {}) };
  const pagination = (data: { total: number; totalCapped: boolean } | undefined): TablePaginationConfig => ({
    current: page,
    pageSize: limit,
    total: data?.total ?? 0,
    showSizeChanger: true,
    pageSizeOptions: [...SIZES],
    // Offsets stop at 10,000.
    showTotal: (total) => (data?.totalCapped ? t("list.totalCapped", { n: formatNumber(total) }) : t("list.total", { n: formatNumber(total) })),
    // A new size starts at page 1 (setting it drops ?page).
    onChange: (p, s) => (String(s) !== size ? setSize(String(s) as Size) : setPageRaw(String(p))),
    hideOnSinglePage: false,
    placement: ["bottomEnd"],
  });
  return { query, pagination, page };
}

/** A created-at range in the URL (?from, ?to as days), sent as instants: to is exclusive. */
export function useDateRange(fromKey = "from", toKey = "to") {
  const [from] = useUrlFilter<string>(fromKey, "");
  const [to] = useUrlFilter<string>(toKey, "");
  const setFilters = useSetUrlFilters();
  const query = {
    ...(from ? { from: dayjs(from, DAY).startOf("day").toISOString() } : {}),
    ...(to ? { to: dayjs(to, DAY).add(1, "day").startOf("day").toISOString() } : {}),
  };
  const value: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null = from || to ? [from ? dayjs(from, DAY) : null, to ? dayjs(to, DAY) : null] : null;
  const set = (v: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setFilters({ [fromKey]: v?.[0] ? v[0].format(DAY) : "", [toKey]: v?.[1] ? v[1].format(DAY) : "" });
  };
  return { query, value, set };
}

/** Client-side paging for lists the API returns whole (settings, bins). */
export const CLIENT_PAGING: TablePaginationConfig = { defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200], hideOnSinglePage: true };
